import { Button, Flex, IconFont } from '@byecode/ui'
import type { AnimateLayoutChanges } from '@dnd-kit/sortable'
import { defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable'
import type { ActorEvent, BackgroundNoImageProtocol, IconDecorationProtocol } from '@lighthouse/core'
import { type BlockAbstract, type RecordLikeProtocol } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import { checkActionEvent, getTextColorStyle, IconRender, useAppThemeContext } from '@lighthouse/shared'
import { AnimatePresence, motion } from 'motion/react'
import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { Item } from '../CustomViewBlock/Item'
import { buildVerticalItemNodeData } from '../CustomViewBlock/utils'

const MotionIconRender = motion(IconRender)

const Wrapper = styled.div<{ gap: number }>`
    display: flex;
    flex-direction: column;
    gap: ${({ gap }) => gap}px;
`
const MotionWrapper = motion.create(Wrapper)

export type TreeRecord = { record: RecordLikeProtocol; children: TreeRecord[]; index: number }

interface TreeItemProps {
    record: RecordLikeProtocol
    index: number
    indent?: number
    align?: 'center' | 'top'
    topMargin?: number
    expandIcon?: { icon?: IconDecorationProtocol; color?: BackgroundNoImageProtocol }
    collapseIcon?: { icon?: IconDecorationProtocol; color?: BackgroundNoImageProtocol }
    gap?: number
    readonly?: boolean
    node: FlowLayoutNode
    blockData: BlockAbstract
    title: string
    isCollapsed: boolean
    onToggleNode: (recordId: string) => void
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    canDragTreeSort?: boolean
    renderOrder?: number
    scrollParent?: HTMLElement | null
    level?: number // Hierarchy level for flat rendering (fixed)
    hasChildren?: boolean // Whether this node has children (for flat rendering)
    showDropIndicator?: boolean // Show drop indicator line
    dropIndent?: number // Drop indicator indent level (dynamic during drag)
}

// Drop指示器线条，显示项目将被插入的位置
const DropIndicator = styled(motion.div)`
    position: absolute;
    left: 0;
    right: 0;
    top: -1px;
    height: 2px;
    background: var(--color-primary, #1890ff);
    border-radius: 1px;
    pointer-events: none;
    z-index: 1000;
`

const EXPAND_BUTTON_WIDTH = 36
const INDENT_SIZE = 28
const EAGER_RENDER_COUNT = 40
const LAZY_ITEM_MIN_HEIGHT = 40
const EMPTY_DRAG_PROPS = {}
const INACTIVE_ITEM_STYLE = {
    minHeight: LAZY_ITEM_MIN_HEIGHT,
    contentVisibility: 'auto',
    containIntrinsicSize: `${LAZY_ITEM_MIN_HEIGHT}px`
} as const

// 自定义树形结构的布局动画变化 - 拖拽时禁用动画
const animateLayoutChanges: AnimateLayoutChanges = args => {
    const { isSorting, wasDragging } = args
    if (isSorting || wasDragging) {
        return false
    }
    return defaultAnimateLayoutChanges(args)
}

export const TreeItem = memo(
    forwardRef<HTMLDivElement, TreeItemProps>((props, ref) => {
        return props.canDragTreeSort ? <SortableTreeItem {...props} ref={ref} /> : <StaticTreeItem {...props} ref={ref} />
    }),
    areTreeItemPropsEqual
)

const StaticTreeItem = memo(
    forwardRef<HTMLDivElement, TreeItemProps>((props, ref) => {
        return <TreeItemView {...props} ref={ref} animateIcon={false} dragAreaProps={EMPTY_DRAG_PROPS} />
    }),
    areTreeItemPropsEqual
)

const SortableTreeItem = memo(
    forwardRef<HTMLDivElement, TreeItemProps>((props, ref) => {
        const { record } = props
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
            id: record.id,
            animateLayoutChanges
        })

        const dragAreaProps = useMemo(() => ({ ...attributes, ...listeners }), [attributes, listeners])

        return (
            <TreeItemView
                {...props}
                ref={ref}
                sortableRef={setNodeRef}
                animateIcon
                dragAreaProps={dragAreaProps}
                isDragging={isDragging}
                transform={transform}
                transition={transition}
            />
        )
    }),
    areTreeItemPropsEqual
)

interface TreeItemViewProps extends TreeItemProps {
    animateIcon?: boolean
    dragAreaProps: Record<string, unknown>
    isDragging?: boolean
    sortableRef?: React.Ref<HTMLDivElement>
    transform?: { x: number; y: number; scaleX?: number; scaleY?: number } | null
    transition?: string
}

const TreeItemView = forwardRef<HTMLDivElement, TreeItemViewProps>(
    (
        {
            record,
            index,
            indent = INDENT_SIZE,
            align,
            topMargin,
            expandIcon,
            collapseIcon,
            gap,
            readonly,
            node,
            blockData,
            title,
            isCollapsed,
            onToggleNode,
            onRecordClick,
            canDragTreeSort,
            renderOrder,
            scrollParent,
            level,
            hasChildren,
            showDropIndicator,
            dropIndent,
            animateIcon = true,
            dragAreaProps,
            isDragging = false,
            sortableRef,
            transform,
            transition,
            ...rest
        },
        ref
    ) => {
        const { theme } = useAppThemeContext()
        const actualIndent = typeof level === 'number' ? level * indent : 0
        const dropIndicatorIndent = (typeof dropIndent === 'number' ? dropIndent * indent : actualIndent) + EXPAND_BUTTON_WIDTH
        const flexStyle = useMemo(
            () => ({
                transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
                transition,
                opacity: isDragging ? 0.5 : 1
            }),
            [isDragging, transform, transition]
        )

        const wasDraggingRef = useRef(false)
        useEffect(() => {
            if (isDragging) {
                wasDraggingRef.current = true
            }
        }, [isDragging])

        const itemCursorStyle = useMemo(
            () => (canDragTreeSort ? { cursor: isDragging ? 'grabbing' : 'grab' } : undefined),
            [canDragTreeSort, isDragging]
        )
        const itemContentStyle = useMemo(
            () => ({
                flex: 1,
                overflow: 'hidden',
                ...itemCursorStyle
            }),
            [itemCursorStyle]
        )
        const itemNodeData = useMemo(
            () =>
                buildVerticalItemNodeData({
                    node,
                    blockId: blockData.id,
                    recordId: record.id,
                    index,
                    readonly,
                    widthSize: {
                        size: 'fill'
                    }
                }),
            [blockData.id, index, node, readonly, record.id]
        )
        const handleRowClick = useCallback(
            (ev: React.MouseEvent<HTMLDivElement>) => {
                if (wasDraggingRef.current) {
                    wasDraggingRef.current = false
                    return
                }

                const path = ev.nativeEvent.composedPath()
                if (isExpandButtonClick(path)) {
                    if (hasChildren) {
                        onToggleNode(record.id)
                    }
                    return
                }

                if (!checkActionEvent(ev)) {
                    return
                }
                if (isOverlayOrTriggerEvent(path, ev.currentTarget)) {
                    return
                }
                if (hasChildren) {
                    onToggleNode(record.id)
                }
            },
            [hasChildren, onToggleNode, record.id]
        )

        const content = (
            <>
                {showDropIndicator && (
                    <DropIndicator
                        initial={{ opacity: 0, scaleX: 0.8 }}
                        animate={{ opacity: 1, scaleX: 1 }}
                        exit={{ opacity: 0, scaleX: 0.8 }}
                        transition={{ duration: 0.2 }}
                        style={{ left: dropIndicatorIndent }}
                    />
                )}
                <Flex
                    data-record-id={record.id}
                    ref={sortableRef}
                    style={{
                        ...flexStyle,
                        paddingLeft: actualIndent
                    }}
                    onClick={handleRowClick}
                >
                    <Button
                        data-expand-button
                        type="text"
                        ghost
                        style={{
                            opacity: hasChildren ? 1 : 0,
                            pointerEvents: hasChildren ? undefined : 'none',
                            padding: '0 4px 0 0',
                            marginTop: topMargin,
                            alignSelf: align === 'center' ? 'center' : 'flex-start'
                        }}
                    >
                        {expandIcon?.icon || collapseIcon?.icon ? (
                            animateIcon ? (
                                <AnimatePresence mode="wait" initial={false}>
                                    {isCollapsed ? (
                                        <MotionIconRender
                                            key="collapsed"
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.8 }}
                                            transition={{ duration: 0.15, ease: 'easeInOut' }}
                                            isCompact
                                            readonly
                                            value={collapseIcon?.icon}
                                            color={getTextColorStyle(collapseIcon?.color, theme.palettes).color}
                                            size={16}
                                        />
                                    ) : (
                                        <MotionIconRender
                                            key="expanded"
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.8 }}
                                            transition={{ duration: 0.15, ease: 'easeInOut' }}
                                            isCompact
                                            readonly
                                            value={expandIcon?.icon}
                                            color={getTextColorStyle(expandIcon?.color, theme.palettes).color}
                                            size={16}
                                        />
                                    )}
                                </AnimatePresence>
                            ) : (
                                <IconRender
                                    isCompact
                                    readonly
                                    value={isCollapsed ? collapseIcon?.icon : expandIcon?.icon}
                                    color={
                                        isCollapsed
                                            ? getTextColorStyle(collapseIcon?.color, theme.palettes).color
                                            : getTextColorStyle(expandIcon?.color, theme.palettes).color
                                    }
                                    size={16}
                                />
                            )
                        ) : (
                            <IconFont
                                type="ArrowDownSmall"
                                size={16}
                                color={
                                    isCollapsed
                                        ? getTextColorStyle(collapseIcon?.color, theme.palettes).color
                                        : getTextColorStyle(expandIcon?.color, theme.palettes).color
                                }
                                style={{
                                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                }}
                            />
                        )}
                    </Button>
                    <div style={itemContentStyle} {...dragAreaProps}>
                        <DeferredTreeItemContent
                            eager={isDragging || (renderOrder ?? 0) < EAGER_RENDER_COUNT}
                            scrollParent={scrollParent}
                            title={title}
                        >
                            <Item
                                recordId={record.id}
                                record={record}
                                recordIndexPath={String(index)}
                                readonly={readonly}
                                data={itemNodeData}
                                title={title}
                                onRecordClick={onRecordClick}
                            />
                        </DeferredTreeItemContent>
                    </div>
                </Flex>
            </>
        )

        if (canDragTreeSort) {
            return (
                <MotionWrapper
                    gap={gap ?? 0}
                    ref={ref}
                    style={{ position: 'relative' }}
                    layout
                    initial={false}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    {...rest}
                >
                    {content}
                </MotionWrapper>
            )
        }

        return (
            <Wrapper gap={gap ?? 0} ref={ref} style={{ position: 'relative' }} {...rest}>
                {content}
            </Wrapper>
        )
    }
)

function isExpandButtonClick(path: EventTarget[]) {
    return path.some(item => item instanceof Element && item.hasAttribute('data-expand-button'))
}

const DeferredTreeItemContent = memo(
    ({
        eager,
        children,
        title,
        scrollParent
    }: {
        eager: boolean
        children: React.ReactNode
        title: string
        scrollParent?: HTMLElement | null
    }) => {
        const containerRef = useRef<HTMLDivElement | null>(null)
        const [isActive, setIsActive] = useState(eager)

        useEffect(() => {
            if (eager && !isActive) {
                setIsActive(true)
            }
        }, [eager, isActive])

        useEffect(() => {
            if (eager || isActive) {
                return
            }

            const element = containerRef.current
            if (!element) {
                return
            }

            const root = scrollParent && scrollParent !== document.body ? scrollParent : null
            const observer = new IntersectionObserver(
                entries => {
                    if (entries.some(entry => entry.isIntersecting || entry.intersectionRatio > 0)) {
                        setIsActive(true)
                        observer.disconnect()
                    }
                },
                { root, rootMargin: '800px 0px' }
            )

            observer.observe(element)
            return () => observer.disconnect()
        }, [eager, isActive, scrollParent])

        return (
            <div ref={containerRef} style={isActive ? undefined : INACTIVE_ITEM_STYLE} aria-label={isActive ? undefined : title}>
                {isActive ? children : null}
            </div>
        )
    }
)

function isOverlayOrTriggerEvent(path: EventTarget[], currentTarget: EventTarget | null) {
    const currentDomIndex = path.indexOf(currentTarget as EventTarget)
    if (currentDomIndex === -1) {
        return false
    }
    const slice = path.slice(0, currentDomIndex)
    for (const item of slice) {
        if (item instanceof Element) {
            const className = item.className
            const classStr =
                typeof className === 'string'
                    ? className
                    : className && typeof className === 'object' && 'baseVal' in className
                    ? (className as any).baseVal
                    : ''
            if (classStr) {
                const lowerClass = classStr.toLowerCase()
                if (
                    lowerClass.includes('popover') ||
                    lowerClass.includes('modal') ||
                    lowerClass.includes('dropdown') ||
                    lowerClass.includes('select') ||
                    lowerClass.includes('tooltip') ||
                    lowerClass.includes('picker') ||
                    lowerClass.includes('menu') ||
                    lowerClass.includes('overlay') ||
                    lowerClass.includes('portal')
                ) {
                    return true
                }
            }
            if (item.hasAttribute('aria-haspopup') || item.hasAttribute('data-stop-action-propagation')) {
                return true
            }
        }
    }
    return false
}

function areTreeItemPropsEqual(prev: Readonly<TreeItemProps>, next: Readonly<TreeItemProps>) {
    return (
        prev.record === next.record &&
        prev.index === next.index &&
        prev.indent === next.indent &&
        prev.align === next.align &&
        prev.topMargin === next.topMargin &&
        prev.expandIcon === next.expandIcon &&
        prev.collapseIcon === next.collapseIcon &&
        prev.gap === next.gap &&
        prev.readonly === next.readonly &&
        prev.node === next.node &&
        prev.blockData === next.blockData &&
        prev.title === next.title &&
        prev.isCollapsed === next.isCollapsed &&
        prev.onToggleNode === next.onToggleNode &&
        prev.onRecordClick === next.onRecordClick &&
        prev.canDragTreeSort === next.canDragTreeSort &&
        prev.renderOrder === next.renderOrder &&
        prev.scrollParent === next.scrollParent &&
        prev.level === next.level &&
        prev.hasChildren === next.hasChildren &&
        prev.showDropIndicator === next.showDropIndicator &&
        prev.dropIndent === next.dropIndent
    )
}
